import "./Product.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { useCart } from "../../context/CartContext";
import { useNotification } from "../../context/NotificationContext";
import {
  PILLOW_PRODUCT_ID,
  PILLOW_NAME,
  PILLOW_COPY,
  getProductName,
  getPillowDescription,
} from "../../content/pillowContent";
import {
  FaCheckCircle,
  FaTruck,
  FaShieldAlt,
  FaCreditCard,
  FaMinus,
  FaPlus,
} from "react-icons/fa";

import pillow1 from "../../assets/images/pillow1.png";
import pillow2 from "../../assets/images/pillow2.png";
import pillow3 from "../../assets/images/pillow3.png";

const pillowImages = [pillow1, pillow2, pillow3];

function Product() {
  const navigate = useNavigate();
  const { addToCart, cartItems } = useCart();
  const { showNotification } = useNotification();

  const [products, setProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(pillow1);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    api
      .get("/products")
      .then((res) => {
        if (!Array.isArray(res.data)) {
          throw new Error("Invalid products response");
        }

        if (active) {
          setProducts(res.data);
        }
      })
      .catch(() => {
        if (active) {
          setError("אירעה שגיאה בטעינת המוצרים");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const product = products.find(
    (item) => Number(item.product_id) === PILLOW_PRODUCT_ID,
  );

  const otherProducts = products.filter(
    (item) => Number(item.product_id) !== PILLOW_PRODUCT_ID,
  );

  const getAvailableQuantity = (item) => {
    const inCart = cartItems.reduce((total, cartItem) => {
      if (String(cartItem.product_id) === String(item.product_id)) {
        return total + (Number(cartItem.quantity) || 0);
      }

      return total;
    }, 0);

    const stock = Math.max(0, Math.floor(Number(item.stock) || 0));

    return Math.max(0, stock - inCart);
  };

  const handleAddToCart = (item, amount) => {
    const token = localStorage.getItem("token");

    if (!token) {
      showNotification("יש להתחבר כדי להוסיף מוצר לסל", "warning");
      navigate("/login");
      return;
    }

    if (
      !Number.isInteger(amount) ||
      amount < 1 ||
      amount > getAvailableQuantity(item)
    ) {
      showNotification("הכמות המבוקשת אינה זמינה במלאי", "warning");
      return;
    }

    addToCart(item, amount);
    navigate("/cart");
  };

  const formatPrice = (price) =>
    Number(price).toLocaleString("he-IL", {
      style: "currency",
      currency: "ILS",
    });

  if (loading) {
    return (
      <main className="product-page" dir="rtl">
        <div className="product-message" role="status">
          טוען את פרטי המוצרים...
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="product-page" dir="rtl">
        <div className="product-message product-error" role="alert">
          {error}
        </div>
      </main>
    );
  }

  const available = product ? getAvailableQuantity(product) : 0;

  const selectedQuantity = Math.min(quantity, available);

  return (
    <main className="product-page" dir="rtl" lang="he">
      {product ? (
        <>
          <section className="product-layout">
            <div className="product-gallery-card">
              <div className="main-image">
                <div className="product-bg" />

                <img src={selectedImage} alt={`${PILLOW_NAME} Cervica`} />
              </div>

              <div className="gallery">
                {pillowImages.map((image, index) => (
                  <button
                    key={image}
                    type="button"
                    className={
                      selectedImage === image ? "thumb active" : "thumb"
                    }
                    onClick={() => setSelectedImage(image)}
                    aria-label={`הצגת תמונת הכרית מספר ${index + 1}`}
                    aria-pressed={selectedImage === image}
                  >
                    <img
                      src={image}
                      alt={`${PILLOW_NAME} Cervica — תמונה ${index + 1}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="product-info">
              <span className="product-tag">Cervica</span>

              <h1>{getProductName(product)}</h1>

              <p>
                {PILLOW_COPY.intro}
              </p>

              <div className="price-row">
                <div className="price">{formatPrice(product.price)}</div>

                <span
                  className={
                    Number(product.stock) > 0 ? "stock" : "stock out-of-stock"
                  }
                >
                  {Number(product.stock) > 0 ? "זמין במלאי" : "אזל מהמלאי"}
                </span>
              </div>

              {available > 0 && (
                <div className="quantity-row">
                  <span>בחירת כמות</span>

                  <div className="quantity-control">
                    <button
                      type="button"
                      disabled={selectedQuantity <= 1}
                      aria-label="הפחתת כמות"
                      onClick={() =>
                        setQuantity(Math.max(1, selectedQuantity - 1))
                      }
                    >
                      <FaMinus />
                    </button>

                    <strong aria-live="polite">{selectedQuantity}</strong>

                    <button
                      type="button"
                      disabled={selectedQuantity >= available}
                      aria-label="הגדלת כמות"
                      onClick={() =>
                        setQuantity(Math.min(available, selectedQuantity + 1))
                      }
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              )}

              <button
                type="button"
                className="add-cart-btn"
                disabled={available <= 0}
                onClick={() =>
                  handleAddToCart(
                    { ...product, image: pillow1 },
                    selectedQuantity,
                  )
                }
              >
                {Number(product.stock) <= 0
                  ? "המוצר אזל מהמלאי"
                  : available <= 0
                    ? "כל הכמות הזמינה כבר בסל"
                    : "הוספה לסל הקניות"}
              </button>

              <div className="trust-grid">
                <div>
                  <FaTruck />
                  <span>משלוח מהיר עד הבית או איסוף עצמי</span>
                </div>

                <div>
                  <FaCreditCard />
                  <span>בחירת אמצעי תשלום בקופה</span>
                </div>

                <div>
                  <FaShieldAlt />
                  <span>מוצר איכותי</span>
                </div>
              </div>
            </div>
          </section>

          <section className="product-details">
            <div className="tabs">
              <button
                type="button"
                className={
                  activeTab === "description" ? "tab active-tab" : "tab"
                }
                onClick={() => setActiveTab("description")}
                aria-pressed={activeTab === "description"}
              >
                תיאור המוצר
              </button>

              <button
                type="button"
                className={activeTab === "specs" ? "tab active-tab" : "tab"}
                onClick={() => setActiveTab("specs")}
                aria-pressed={activeTab === "specs"}
              >
                מפרט המוצר
              </button>

              <button
                type="button"
                className={activeTab === "shipping" ? "tab active-tab" : "tab"}
                onClick={() => setActiveTab("shipping")}
                aria-pressed={activeTab === "shipping"}
              >
                משלוחים ואיסוף
              </button>
            </div>

            <div className="tab-content">
              {activeTab === "description" && (
                <div className="product-description">
                  {getPillowDescription(product).split(/\r?\n\s*\r?\n/).map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              )}

              {activeTab === "specs" && (
                <ul>
                  <li>
                    <FaCheckCircle />
                    <span>מתאימה לשינה על הגב ועל הצד</span>
                  </li>

                  <li>
                    <FaCheckCircle />
                    <span>חומר ויסקו איכותי שמתאים את עצמו לצורת הצוואר והראש</span>
                  </li>

                  <li>
                    <FaCheckCircle />
                    <span>תמיכה יציבה ונעימה באזור הצוואר והכתפיים</span>
                  </li>

                  <li>
                    <FaCheckCircle />
                    <span>עיצוב גלי ייחודי לשמירה על יישור נכון של עמוד השדרה</span>
                  </li>
                </ul>
              )}

              {activeTab === "shipping" && (
                <p>
                  משלוח מהיר עד הבית או איסוף עצמי, לבחירתכם. זמן האספקה
                  המשוער הוא בין 2 ל־5 ימי עסקים, בהתאם לאזור המשלוח.
                </p>
              )}
            </div>
          </section>
        </>
      ) : (
        <div className="product-message">הכרית אינה זמינה כרגע.</div>
      )}

      {otherProducts.length > 0 && (
        <section className="product-details">
          <h2>מוצרים נוספים</h2>

          <div className="additional-products-grid">
            {otherProducts.map((item) => {
              const remaining = getAvailableQuantity(item);

              return (
                <article
                  key={item.product_id}
                  className="additional-product-card"
                >
                  <h3>{item.name}</h3>

                  <p>{item.description || "פרטים נוספים יעודכנו בקרוב."}</p>

                  <strong className="price">{formatPrice(item.price)}</strong>

                  <button
                    type="button"
                    className="add-cart-btn"
                    disabled={remaining <= 0}
                    onClick={() => handleAddToCart(item, 1)}
                  >
                    {Number(item.stock) <= 0
                      ? "אזל מהמלאי"
                      : remaining <= 0
                        ? "כל הכמות הזמינה כבר בסל"
                        : "הוספה לסל הקניות"}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}

export default Product;
